import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { logout } from '../store/slices/authSlice';
import { useSystemSettings, defaultSystemSettings } from '../contexts/SystemSettingsContext';
import { 
  Users, 
  Database,
  Calendar,
  Settings,
  LogOut, 
  Menu, 
  X,
  Shield,
  Sliders,
  Archive,
  Network,
  ChevronRight,
  ChevronDown,
  LayoutDashboard,
  User as UserIcon,
  Search
} from 'lucide-react';

const AdminLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { settings } = useSystemSettings();
  
  const siteName = settings?.siteName || defaultSystemSettings.siteName;

  const adminNavigation = [
    { name: 'Dashboard', href: '/admin', icon: Settings },
    { name: 'Users', href: '/admin/users', icon: Users },
    { name: 'FIWARE Context Broker', href: '/admin/platform', icon: Network },
    { name: 'Events', href: '/admin/events', icon: Calendar },
    { name: 'Elasticsearch', href: '/admin/elasticsearch', icon: Database },
    { name: 'Backup & Restore', href: '/admin/backup-restore', icon: Archive },
    { name: 'Settings', href: '/admin/all-settings', icon: Sliders },
  ];

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return names[0][0].toUpperCase();
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking inside any user-menu element (toggle button or dropdown)
      if (target.closest('[data-user-menu]')) {
        return;
      }
      setUserMenuOpen(null);
    };
    if (userMenuOpen) {
      // Use setTimeout to avoid the opening click from immediately closing the menu
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [userMenuOpen]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Mobile sidebar overlay */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white shadow-2xl">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <span className="sr-only">Close sidebar</span>
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          {/* Mobile sidebar header */}
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <Link to="/" className="flex-shrink-0 flex items-center px-4 pb-4 border-b border-gray-100 hover:bg-gray-50 transition-colors">
              <div className="flex items-center space-x-3">
                <img src="/logolivinglab.png" alt="Living Lab Logo" className="h-10 w-auto object-contain" />
                <div>
                  <h1 className="text-lg font-bold text-gray-900">Admin</h1>
                  <p className="text-xs text-gray-500">{siteName}</p>
                </div>
              </div>
            </Link>
            <nav className="mt-4 px-3 space-y-1">
              {adminNavigation.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      active
                        ? 'bg-emerald-50 text-emerald-700 font-semibold'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    } group flex items-center px-3 py-2.5 text-sm rounded-xl transition-all duration-200`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className={`mr-3 h-5 w-5 ${active ? 'text-emerald-600' : 'text-gray-400'}`} />
                    {item.name}
                    {active && <ChevronRight className="ml-auto h-4 w-4 text-emerald-500" />}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="flex-shrink-0 border-t border-gray-100 p-4">
            <div className="relative" data-user-menu>
              <button
                type="button"
                onClick={() => setUserMenuOpen(userMenuOpen === 'mobile-sidebar' ? null : 'mobile-sidebar')}
                className="flex items-center w-full rounded-lg p-1 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              >
                <div className="flex-shrink-0">
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-sm">
                    <span className="text-sm font-bold text-white">
                      {getUserInitials()}
                    </span>
                  </div>
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                  <p className="text-xs text-emerald-600 font-medium">Administrator</p>
                </div>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${userMenuOpen === 'mobile-sidebar' ? 'rotate-180' : ''}`} />
              </button>
              {userMenuOpen === 'mobile-sidebar' && (
                <div className="absolute bottom-full left-0 right-0 mb-2 rounded-xl bg-white shadow-lg ring-1 ring-black/5 border border-gray-100 py-1 z-50">
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={() => { setUserMenuOpen(null); setSidebarOpen(false); navigate('/dashboard'); }}
                    className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    Dashboard
                  </button>
                  <button
                    onClick={() => { setUserMenuOpen(null); setSidebarOpen(false); navigate('/profile'); }}
                    className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                  >
                    <UserIcon className="h-4 w-4" />
                    Profile
                  </button>
                  <button
                    onClick={() => { setUserMenuOpen(null); setSidebarOpen(false); navigate('/admin'); }}
                    className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                  >
                    <Shield className="h-4 w-4" />
                    Admin Panel
                  </button>
                  <div className="border-t border-gray-100 my-1" />
                  <button
                    onClick={() => { setUserMenuOpen(null); setSidebarOpen(false); handleLogout(); }}
                    className="flex items-center gap-3 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-72">
          <div className="flex flex-col h-0 flex-1 bg-white border-r border-gray-100 shadow-sm">
            <div className="flex-1 flex flex-col pt-6 pb-4 overflow-y-auto">
              {/* Logo / Brand */}
              <Link to="/" className="flex items-center flex-shrink-0 px-5 pb-5 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-3">
                  <img src="/logolivinglab.png" alt="Living Lab Logo" className="h-10 w-auto object-contain" />
                  <div>
                    <h1 className="text-lg font-bold text-gray-900">Admin Panel</h1>
                    <p className="text-xs text-gray-500">{siteName}</p>
                  </div>
                </div>
              </Link>
              <nav className="mt-5 flex-1 px-3 space-y-1">
                {adminNavigation.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`${
                        active
                          ? 'bg-emerald-50 text-emerald-700 font-semibold shadow-sm'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      } group flex items-center px-3 py-2.5 text-sm rounded-xl transition-all duration-200`}
                    >
                      <Icon className={`mr-3 h-5 w-5 ${active ? 'text-emerald-600' : 'text-gray-400 group-hover:text-gray-500'}`} />
                      {item.name}
                      {active && <ChevronRight className="ml-auto h-4 w-4 text-emerald-500" />}
                    </Link>
                  );
                })}
              </nav>
            </div>
            <div className="flex-shrink-0 border-t border-gray-100 p-4">
              <div className="relative" data-user-menu>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen(userMenuOpen === 'desktop-sidebar' ? null : 'desktop-sidebar')}
                  className="flex items-center w-full rounded-lg p-1 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                >
                  <div className="flex-shrink-0">
                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-sm">
                      <span className="text-sm font-bold text-white">
                        {getUserInitials()}
                      </span>
                    </div>
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                    <p className="text-xs text-emerald-600 font-medium">Administrator</p>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${userMenuOpen === 'desktop-sidebar' ? 'rotate-180' : ''}`} />
                </button>
                {userMenuOpen === 'desktop-sidebar' && (
                  <div className="absolute bottom-full left-0 right-0 mb-2 rounded-xl bg-white shadow-lg ring-1 ring-black/5 border border-gray-100 py-1 z-50">
                    <div className="px-3 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                      <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                    </div>
                    <button
                      onClick={() => { setUserMenuOpen(null); navigate('/dashboard'); }}
                      className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      Dashboard
                    </button>
                    <button
                      onClick={() => { setUserMenuOpen(null); navigate('/profile'); }}
                      className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                    >
                      <UserIcon className="h-4 w-4" />
                      Profile
                    </button>
                    <button
                      onClick={() => { setUserMenuOpen(null); navigate('/admin'); }}
                      className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                    >
                      <Shield className="h-4 w-4" />
                      Admin Panel
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={() => { setUserMenuOpen(null); handleLogout(); }}
                      className="flex items-center gap-3 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top bar */}
        <div className="relative z-[100] flex-shrink-0 flex h-14 border-b border-slate-200 bg-white shadow-sm">
          <button
            type="button"
            className="px-4 border-r border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-emerald-500/30 lg:hidden transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>
          <Link to="/" className="flex items-center gap-2.5 px-4 lg:px-6 border-r border-slate-200 hover:bg-slate-50 transition-colors flex-shrink-0">
            <img src="/logolivinglab.png" alt="Living Lab Logo" className="h-8 w-auto object-contain" />
            <span className="hidden sm:inline font-bold text-lg leading-tight tracking-tight">
              <span className="text-[#c1a927]">E-</span><span className="text-[#66b960]">Living</span><span className="text-[#346a44]"> Lab</span>
            </span>
          </Link>
          <div className="flex-1 flex items-center px-4 lg:px-6">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <input
                type="text"
                placeholder="Search…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg text-slate-900 placeholder-slate-500 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-400 transition-all shadow-sm"
              />
            </div>
          </div>
          <div className="hidden lg:flex items-center gap-2 px-4">
            <div className="flex items-center space-x-2 px-3 py-1.5 bg-emerald-50 rounded-full">
              <Shield className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">Admin</span>
            </div>
          </div>
          <div data-user-menu className="relative ml-2 pl-4 border-l border-slate-200">
            <button
              type="button"
              onClick={() => setUserMenuOpen(userMenuOpen === 'desktop-header' ? null : 'desktop-header')}
              className="flex items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            >
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-sm ring-1 ring-emerald-200/50">
                <span className="text-sm font-semibold text-white">{getUserInitials()}</span>
              </div>
              <div className="flex flex-col items-start">
                <span className="text-sm font-medium text-slate-900">{user?.name || 'User'}</span>
                <span className="text-xs text-slate-500">Administrator</span>
              </div>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${userMenuOpen === 'desktop-header' ? 'rotate-180' : ''}`} />
            </button>
            {userMenuOpen === 'desktop-header' && (
              <div className="absolute right-0 top-full mt-2 w-56 rounded-xl bg-white shadow-lg ring-1 ring-black/5 border border-gray-100 py-1 z-50">
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                </div>
                <button
                  onClick={() => { setUserMenuOpen(null); navigate('/dashboard'); }}
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  Dashboard
                </button>
                <button
                  onClick={() => { setUserMenuOpen(null); navigate('/profile'); }}
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                >
                  <UserIcon className="h-4 w-4" />
                  Profile
                </button>
                <button
                  onClick={() => { setUserMenuOpen(null); navigate('/admin'); }}
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                >
                  <Shield className="h-4 w-4" />
                  Admin Panel
                </button>
                <div className="border-t border-gray-100 my-1" />
                <button
                  onClick={() => { setUserMenuOpen(null); handleLogout(); }}
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            )}
          </div>
          <div className="flex lg:hidden items-center gap-2 px-4">
            <div className="flex items-center space-x-2 px-3 py-1.5 bg-emerald-50 rounded-full">
              <Shield className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">Admin</span>
            </div>
            <button
              type="button"
              data-user-menu
              onClick={() => setUserMenuOpen(userMenuOpen === 'mobile-header' ? null : 'mobile-header')}
              className="relative h-8 w-8 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center ring-1 ring-emerald-200/50 shadow-sm hover:ring-2 hover:ring-emerald-400/50 transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            >
              <span className="text-xs font-semibold text-white">
                {getUserInitials()}
              </span>
            </button>
            {userMenuOpen === 'mobile-header' && (
              <div className="absolute right-4 top-full mt-2 w-56 rounded-xl bg-white shadow-lg ring-1 ring-black/5 border border-gray-100 py-1 z-50">
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                </div>
                <button
                  onClick={() => { setUserMenuOpen(null); navigate('/dashboard'); }}
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  Dashboard
                </button>
                <button
                  onClick={() => { setUserMenuOpen(null); navigate('/profile'); }}
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                >
                  <UserIcon className="h-4 w-4" />
                  Profile
                </button>
                <button
                  onClick={() => { setUserMenuOpen(null); navigate('/admin'); }}
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                >
                  <Shield className="h-4 w-4" />
                  Admin Panel
                </button>
                <div className="border-t border-gray-100 my-1" />
                <button
                  onClick={() => { setUserMenuOpen(null); handleLogout(); }}
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>

        <main className="flex-1 relative overflow-y-auto focus:outline-none min-h-0">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
